import type { KyInstance } from 'ky'
import ky from 'ky'
import type { Server } from './types'
import type { UserInfoSource } from './user-info-source'

export interface PaywallApi {
  readonly infra: KyInstance
  readonly receipt: KyInstance
  readonly service: KyInstance
}

export interface CreatePaywallApiOptions {
  userInfo: UserInfoSource
  infraBaseByServer?: Record<Server, string>
  receiptBaseByServer?: Record<Server, string>
  serviceBaseByServer?: Record<Server, string>
}

export const DEFAULT_SERVICE_BASE_URLS: Record<Server, string> = {
  dev: 'https://forest-api.service.develop-cluster.seekrtech.com',
  staging: 'https://forest-api-staging.seekrtech.com',
  global: 'https://forest-api.seekrtech.com',
  china: 'https://forest-api.upwardsware.com',
}

export const DEFAULT_RECEIPT_BASE_URLS: Record<Server, string> = {
  dev: 'https://receipt.service.develop-cluster.seekrtech.com',
  staging: 'https://receipt.staging.seekrtech.com',
  global: 'https://receipt-system.seekrtech.com',
  china: 'https://receipt-china.upwardsware.com',
}

/** The Infra API hosts. Each of its services sits under its own path prefix. */
export const DEFAULT_INFRA_BASE_URLS: Record<Server, string> = {
  dev: 'https://infra-api.service.develop-cluster.seekrtech.com',
  staging: 'https://infra-api-staging.seekrtech.com',
  global: 'https://infra-api.seekrtech.com',
  china: 'https://infra-api.upwardsware.com',
}

const SERVICE_PLACEHOLDER = 'https://__paywall_service__.invalid/'
const RECEIPT_PLACEHOLDER = 'https://__paywall_receipt__.invalid/'
const INFRA_PLACEHOLDER = 'https://__paywall_infra__.invalid/'

export function createPaywallApi(options: CreatePaywallApiOptions): PaywallApi {
  const { userInfo } = options
  const serviceServerUrl =
    options.serviceBaseByServer ?? DEFAULT_SERVICE_BASE_URLS
  const receiptSystemUrl =
    options.receiptBaseByServer ?? DEFAULT_RECEIPT_BASE_URLS
  const infraApiUrl = options.infraBaseByServer ?? DEFAULT_INFRA_BASE_URLS

  const base = ky.create({
    timeout: 20_000,
    hooks: {
      beforeRequest: [
        async (request) => {
          const value = await userInfo.getReady()

          if (value.user_type === 'legacy') {
            request.headers.set('Remember-Token', value.token)
          } else {
            request.headers.set('Authorization', `Bearer ${value.token}`)
          }

          let realBase: string | undefined
          let path: string | undefined
          if (request.url.startsWith(SERVICE_PLACEHOLDER)) {
            path = request.url.slice(SERVICE_PLACEHOLDER.length)
            realBase = serviceServerUrl[value.server]
          } else if (request.url.startsWith(RECEIPT_PLACEHOLDER)) {
            path = request.url.slice(RECEIPT_PLACEHOLDER.length)
            realBase = receiptSystemUrl[value.server]
          } else if (request.url.startsWith(INFRA_PLACEHOLDER)) {
            path = request.url.slice(INFRA_PLACEHOLDER.length)
            realBase = infraApiUrl[value.server]
          }
          if (realBase != null && path !== undefined) {
            // Some WebViews do not expose Request.body even when clone() can
            // still read the request payload.
            const body =
              request.method === 'GET' || request.method === 'HEAD'
                ? undefined
                : await request.clone().arrayBuffer()
            return new Request(`${realBase.replace(/\/$/, '')}/${path}`, {
              method: request.method,
              headers: request.headers,
              body,
              signal: request.signal,
              credentials: request.credentials,
              cache: request.cache,
              mode: request.mode,
              redirect: request.redirect,
              referrer: request.referrer,
              referrerPolicy: request.referrerPolicy,
              integrity: request.integrity,
              keepalive: request.keepalive,
            })
          }
          return undefined
        },
      ],
      afterResponse: [
        async (request, _, response) => {
          if (response.status === 401 || response.status === 403) {
            const value = userInfo.get()
            if (!value) throw new Error('User info not set')
            if (value.user_type === 'legacy') return response
            const refreshed = await userInfo.refresh()
            request.headers.set('Authorization', `Bearer ${refreshed.token}`)
            return await ky(request)
          }
          return response
        },
      ],
    },
  })

  return {
    service: base.extend({ prefixUrl: SERVICE_PLACEHOLDER }),
    receipt: base.extend({ prefixUrl: RECEIPT_PLACEHOLDER }),
    infra: base.extend({ prefixUrl: INFRA_PLACEHOLDER }),
  }
}
